import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "../apiClientContext";
import { useKeepAdjacentData } from "../queryPlaceholders";
import { fetchCompletedWorkItemsByQueue, fetchMyWorkItems, fetchWorkItemsByQueue } from "./api";
import type { CompletedWorkItemPageSize, WorkItemBucket } from "./api";
import type { WorkItemListResponse } from "./types";

const workItemKeys = {
    // The bucket is the last key segment so the realtime layer's
    // prefix-based reconciliation (`["workitems","byQueue"]` in
    // cachePatches.ts / useQueueRealtime.ts) still matches every bucketed
    // variant: an Added/Due invalidation refetches the bucket lists and a
    // Completed patch removes the row from them, with no awareness of the
    // bucket dimension.
    byQueue: (
        queueId: string,
        view: QueueWorkItemView,
        skip: number,
        take: number,
        bucket: WorkItemBucket | undefined,
    ) => ["workitems", "byQueue", queueId, view, skip, take, bucket ?? null] as const,
    mine: (includeCompleted: boolean, skip: number, take: number) =>
        ["workitems", "mine", includeCompleted, skip, take] as const,
};

// Queue worklists are for actionable work — tasks and messages routed to
// the queue. Notes are workflow-internal annotations that ride along on
// a workflow that may happen to carry the queue tag; surfacing them in
// the worklist clutters the "what's left to do" answer the queue view is
// supposed to give. Filtering happens via react-query's `select`, so the
// raw cache still mirrors the BFF response and realtime patches in
// `cachePatches.ts` keep applying without any awareness of the policy.
const DEFAULT_EXCLUDED_TYPES: readonly string[] = ["note"];

export type QueueWorkItemView = "active" | "completed";

interface UseWorkItemsByQueueBaseOptions {
    readonly queueId: string;
    readonly skip?: number;
    /**
     * Work item types to drop from the rendered list. Defaults to
     * <c>["note"]</c>; pass an empty array to opt out of all filtering
     * (e.g. an audit surface that wants the raw routing picture).
     */
    readonly excludeTypes?: readonly string[];
}

export type UseWorkItemsByQueueOptions = UseWorkItemsByQueueBaseOptions &
    (
        | {
              readonly view: "active";
              readonly take?: number;
              /**
               * Narrows active work to one lifecycle bucket.
               */
              readonly bucket?: WorkItemBucket;
          }
        | {
              readonly view: "completed";
              readonly take?: CompletedWorkItemPageSize;
              readonly bucket?: never;
          }
    );

export function useWorkItemsByQueue(options: UseWorkItemsByQueueOptions) {
    const { queueId, view, skip = 0, excludeTypes = DEFAULT_EXCLUDED_TYPES } = options;
    const take = options.take ?? (view === "completed" ? 20 : 50);
    /**
     * Active work is responsibility-based; completed history is
     * visibility-based. They deliberately use separate endpoints so neither
     * query can silently inherit the other surface's membership semantics.
     */
    const client = useApiClient();
    // The select identity matters: react-query reruns it on every render,
    // but a stable callback lets it skip downstream re-render churn when
    // the underlying data is unchanged. We close over `excludeTypes` by
    // value via a join key so unrelated caller renders don't bust it.
    const excludeKey = excludeTypes.join("|");
    const select = useCallback(
        (data: WorkItemListResponse): WorkItemListResponse => {
            if (excludeKey.length === 0) return data;
            const excluded = new Set(excludeKey.split("|"));
            const filtered = data.items.filter((i) => !excluded.has(i.type));
            if (filtered.length === data.items.length) return data;
            // Completed history excludes notes server-side, so its total is
            // authoritative. This fallback remains for custom exclusions and
            // the unpaginated active view, where the server cannot tell how
            // many client-selected types exist beyond the returned page.
            return { ...data, items: filtered, totalCount: filtered.length };
        },
        [excludeKey],
    );
    const enabled = queueId.length > 0;
    const queryKey = workItemKeys.byQueue(queueId, view, skip, take, options.bucket);
    // Changing queue, view, or page keeps the previous list on screen, dimmed,
    // until the next page arrives — never a skeleton over a populated column.
    const placeholder = useKeepAdjacentData(queryKey, enabled);
    return useQuery({
        queryKey,
        queryFn: () => {
            if (options.view === "completed") {
                return fetchCompletedWorkItemsByQueue(client, {
                    queueId,
                    skip,
                    take: options.take ?? 20,
                });
            }
            return fetchWorkItemsByQueue(client, {
                queueId,
                includeCompleted: false,
                skip,
                take,
                ...(options.bucket !== undefined ? { bucket: options.bucket } : {}),
            });
        },
        enabled,
        staleTime: 15_000,
        // Focus refetch is kept on purpose: the worklist is the "what is left
        // to do" answer, and a tab that was in the background may have missed
        // hub events. It is safe for the eye because rows key by id, stagger
        // runs only on the first paint, and data-present refetches never
        // show a skeleton — a focus refetch can only fade in new rows.
        refetchOnWindowFocus: true,
        select,
        ...placeholder,
    });
}

export interface UseMyWorkItemsOptions {
    readonly includeCompleted: boolean;
    readonly skip?: number;
    readonly take?: number;
    /**
     * Periodic re-read cadence in ms while the document is visible, or `false`
     * (the default) for no polling. React Query pauses it when the document is
     * hidden (refetchIntervalInBackground stays false), so an idle background
     * window costs nothing.
     */
    readonly refetchInterval?: number | false;
}

export function useMyWorkItems({
    includeCompleted,
    skip = 0,
    take = 50,
    refetchInterval = false,
}: UseMyWorkItemsOptions) {
    const client = useApiClient();
    return useQuery({
        queryKey: workItemKeys.mine(includeCompleted, skip, take),
        queryFn: () => fetchMyWorkItems(client, { includeCompleted, skip, take }),
        staleTime: 15_000,
        refetchOnWindowFocus: true,
        refetchInterval,
        refetchIntervalInBackground: false,
    });
}
